import * as msal from "@azure/msal-browser";
import store from '../store';
import { UserInfo } from "./GraphResponseTypes";

/**
 * Configuration class for @azure/msal-browser: 
 * https://azuread.github.io/microsoft-authentication-library-for-js/ref/msal-browser/modules/_src_config_configuration_.html
 */
export const authConfiguration = {
    auth: {
        clientId: "b5f0d2a6-412b-41b8-851e-ac037b34bf6e",
        authority: "https://login.microsoftonline.com/b2eddca8-d3e0-4fdb-8d75-43ebb0add624"
    },
    cache: {
        cacheLocation: "sessionStorage", // This configures where your cache will be stored
        storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
    },
    system: {
        loggerOptions: {
            loggerCallback: (level, message, containsPii) => {
                if (containsPii) {
                    return;
                }
                switch (level) {
                    case msal.LogLevel.Error:
                        console.error(message);
                        return;
                    case msal.LogLevel.Info:
                        console.info(message);
                        return;
                    case msal.LogLevel.Verbose:
                        console.debug(message);
                        return;
                    case msal.LogLevel.Warning:
                        console.warn(message);
                        return;
                }
            }
        }
    }
};

export { Configuration, AccountInfo } from "@azure/msal-browser";
export { UserInfo };

/**
 * AuthModule for application - handles authentication in app.
 */
export class AuthModule {

    private DownloadScope: string = "api://e8076799-6a12-4e36-a718-b101db9dfd9f/download-software";

    private msal: msal.PublicClientApplication; // https://azuread.github.io/microsoft-authentication-library-for-js/ref/msal-browser/classes/_src_app_publicclientapplication_.publicclientapplication.html
    public account: msal.AccountInfo; // https://azuread.github.io/microsoft-authentication-library-for-js/ref/msal-common/modules/_src_account_accountinfo_.html
    private loginRedirectRequest: msal.RedirectRequest; // https://azuread.github.io/microsoft-authentication-library-for-js/ref/msal-browser/modules/_src_request_redirectrequest_.html
    private loginRequest: msal.PopupRequest; // https://azuread.github.io/microsoft-authentication-library-for-js/ref/msal-browser/modules/_src_request_popuprequest_.html
    private profileRedirectRequest: msal.RedirectRequest;
    private profileRequest: msal.PopupRequest;
    private mailRedirectRequest: msal.RedirectRequest;
    private downloadRedirectRequest: msal.RedirectRequest;
    private mailRequest: msal.PopupRequest;
    private silentProfileRequest: msal.SilentRequest; // https://azuread.github.io/microsoft-authentication-library-for-js/ref/msal-browser/modules/_src_request_silentrequest_.html
    private silentMailRequest: msal.SilentRequest;
    private silentDownloadRequest: msal.SilentRequest;

    constructor(config: msal.Configuration, scopes: string[]) {
        this.msal = new msal.PublicClientApplication(config);
        this.account = null;
        this.setRequestObjects(scopes);
    }

    /**
     * Initialize request objects used by this AuthModule.
     */
    private setRequestObjects(scopes: string[]): void {
        this.loginRequest = {
            scopes: []
        };

        this.loginRedirectRequest = {
            ...this.loginRequest,
            redirectStartPage: window.location.href
        };

        this.profileRequest = {
            scopes: ["User.Read"]
        };

        this.profileRedirectRequest = {
            ...this.profileRequest,
            redirectStartPage: window.location.href
        };

        // Add here scopes for access token to be used at MS Graph API endpoints.
        this.mailRequest = {
            scopes: ["Mail.Read"]
        };

        this.mailRedirectRequest = {
            ...this.mailRequest,
            redirectStartPage: window.location.href
        };

        this.downloadRedirectRequest = {
            scopes: [this.DownloadScope],
            redirectStartPage: window.location.href
        };

        this.silentProfileRequest = {
            scopes: ["openid", "profile", "User.Read"],
            account: null,
            forceRefresh: false
        };

        this.silentMailRequest = {
            scopes: ["openid", "profile", "Mail.Read"],
            account: null,
            forceRefresh: false
        };

        this.silentDownloadRequest = {
            scopes: ["openid", this.DownloadScope],
            account: null,
            forceRefresh: false
        };
    }

    /**
     * Calls getAllAccounts and determines the correct account to sign into, currently defaults to first account found in cache.
     * TODO: Add account chooser code
     * 
     * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-common/docs/Accounts.md
     */
    private getAccount(): msal.AccountInfo {
        // need to call getAccount here?
        const currentAccounts = this.msal.getAllAccounts();
        if (currentAccounts === null) {
            console.log("No accounts detected");
            return null;
        }

        if (currentAccounts.length > 1) {
            // Add choose account code here
            console.log("Multiple accounts detected, need to add choose account code.");
            return currentAccounts[0];
        } else if (currentAccounts.length === 1) {
            return currentAccounts[0];
        }
    }

    /**
     * Checks whether we are in the middle of a redirect and handles state accordingly. Only required for redirect flows.
     * 
     * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/initialization.md#redirect-apis
     */
    loadAuthModule(): void {
        this.msal.handleRedirectPromise().then((resp: msal.AuthenticationResult) => {
            this.handleResponse(resp);
        }).catch(console.error);
    }

    /**
     * Handles the response from a popup or redirect. If response is null, will check if we have any accounts and attempt to sign in.
     * @param response 
     */
    handleResponse(response: msal.AuthenticationResult) {
        if (response !== null) {
            this.account = response.account;
        } else {
            this.account = this.getAccount();
        }

        if (this.account) {
            store.commit("SET_ACCOUNT", this.account)
        }
        else {
            store.dispatch("LOGOUT");
        }
    }

    /**
     * Calls loginPopup or loginRedirect based on given signInType.
     * @param signInType 
     */
    public login(signInType: LogInType): void {
        if (signInType == LogInType.LOGIN_POPUP) {
            this.msal.loginPopup(this.loginRequest).then((resp: msal.AuthenticationResult) => {
                this.handleResponse(resp);
            }).catch(console.error);
        } else if (signInType == LogInType.LOGIN_REDIRECT) {
            this.msal.loginRedirect(this.loginRedirectRequest);
        }
    }

    /**
     * Logs out of current account.
     */
    public logout(): void {
        const logOutRequest: msal.EndSessionRequest = {
            account: this.account
        };

        this.msal.logout(logOutRequest);
    }

    /**
     * Gets the token to read user profile data from MS Graph silently, or falls back to interactive redirect.
     */
    public async getProfileTokenRedirect(): Promise<string> {
        this.silentProfileRequest.account = this.account;
        return this.getTokenRedirect(this.silentProfileRequest, this.profileRedirectRequest);
    }

    /**
     * Gets the token to read user profile data from MS Graph silently, or falls back to interactive popup.
     */
    public async getProfileTokenPopup(): Promise<string> {
        this.silentProfileRequest.account = this.account;
        return this.getTokenPopup(this.silentProfileRequest, this.profileRequest);
    }

    /**
     * Gets the token to read mail data from MS Graph silently, or falls back to interactive redirect.
     */
    public async getMailTokenRedirect(): Promise<string> {
        this.silentMailRequest.account = this.account;
        return this.getTokenRedirect(this.silentMailRequest, this.mailRedirectRequest);
    }

    public async getDownloadTokenRedirect(): Promise<string> {
        this.silentDownloadRequest.account = this.account;
        return this.getTokenRedirect(this.silentDownloadRequest, this.downloadRedirectRequest);
    }

    /**
     * Gets the token to read mail data from MS Graph silently, or falls back to interactive popup.
     */
    public async getMailTokenPopup(): Promise<string> {
        this.silentMailRequest.account = this.account;
        return this.getTokenPopup(this.silentMailRequest, this.mailRequest);
    }

    /**
     * Gets a token silently, or falls back to interactive popup.
     */
    private async getTokenPopup(silentRequest: msal.SilentRequest, interactiveRequest: msal.PopupRequest): Promise<string> {
        try {
            const response: msal.AuthenticationResult = await this.msal.acquireTokenSilent(silentRequest);
            return response.accessToken;
        } catch (e) {
            console.log("silent token acquisition fails.");
            if (e instanceof msal.InteractionRequiredAuthError) {
                console.log("acquiring token using redirect");
                return this.msal.acquireTokenPopup(interactiveRequest).then((resp) => {
                    return resp.accessToken;
                }).catch((err) => {
                    console.error(err);
                    return null;
                });
            } else {
                console.error(e);
            }
        }
    }

    /**
     * Gets a token silently, or falls back to interactive redirect.
     */
    private async getTokenRedirect(silentRequest: msal.SilentRequest, interactiveRequest: msal.RedirectRequest): Promise<string> {
        try {
            const response = await this.msal.acquireTokenSilent(silentRequest);
            return response.accessToken;
        } catch (e) {
            console.log("silent token acquisition fails.");
            if (e instanceof msal.InteractionRequiredAuthError) {
                console.log("acquiring token using redirect");
                this.msal.acquireTokenRedirect(interactiveRequest).catch(console.error);
            } else {
                console.error(e);
            }
        }
    }
}

export enum LogInType {
    LOGIN_REDIRECT,
    LOGIN_POPUP,
}